import {
  Component,
  Inject,
  AfterViewInit,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

export interface MudMeasureDialogData {
  preferredMudContainer?: string;
  measuredMudContainer?: string;
  mudContainerComments?: string;
}

export interface MudMeasureDialogResult {
  measuredMudContainer?: string;
  mudContainerComments?: string;
  replacePreferredContainer?: boolean;
}

@Component({
  selector: 'app-mud-measure-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './mud-measure-dialog.component.html',
  styleUrls: ['./mud-measure-dialog.component.css']
})
export class MudMeasureDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly scannerInterKeyTimeoutMs = 500;
  private readonly scannerMaximumAverageInterKeyMs = 80;
  private readonly scannerMinimumLength = 4;

  @ViewChild('capturedInput') capturedInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scannerInput') scannerInput?: ElementRef<HTMLInputElement>;

  measuredMudContainer = '';
  mudContainerComments = '';
  replacePreferredContainer = false;
  enterManually = false;
  containerEntered = false;
  private destroyed = false;

  // Scanner buffer
  private scanBuffer = '';
  private scanBufferResetHandle: ReturnType<typeof setTimeout> | null = null;
  private scannerFirstCharacterAt: number | null = null;
  private scannerLastCharacterAt: number | null = null;

  constructor(
    public dialogRef: MatDialogRef<MudMeasureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MudMeasureDialogData,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Pre-populate with existing values
    this.measuredMudContainer = this.data.measuredMudContainer || '';
    this.mudContainerComments = this.data.mudContainerComments || '';
    
    // If already has data, mark as entered
    if (this.measuredMudContainer) {
      this.containerEntered = true;
    }
  }

  ngAfterViewInit(): void {
    this.focusScannerTarget();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearScannerBuffer();
  }

  get showStatusBanner(): boolean {
    return !this.containerEntered;
  }

  get isCapturingBarcode(): boolean {
    return !this.containerEntered;
  }

  get canEnterManually(): boolean {
    return this.isCapturingBarcode && (!this.enterManually || !!this.measuredMudContainer?.trim());
  }

  onEnterManually(): void {
    if (!this.canEnterManually) return;

    if (!this.enterManually) {
      this.clearScannerBuffer();
      this.enterManually = true;
      this.cdr.markForCheck();
      this.focusScannerTarget();
      return;
    }

    this.onBarcodeEntered();
  }

  onBarcodeEntered(): void {
    if (!this.isCapturingBarcode) return;
    const barcode = this.normalizeBarcode(this.measuredMudContainer);
    if (!barcode) return;
    this.acceptCapturedBarcode(barcode);
  }

  onContainerChanged(): void {
    this.measuredMudContainer = this.normalizeBarcode(this.measuredMudContainer);
    if (this.enterManually) {
      this.scanBuffer = this.measuredMudContainer;
    }
    // Clear comments if container becomes acceptable
    if (this.isContainerAcceptable) {
      this.mudContainerComments = '';
    }
  }

  // ── Scanner methods ────────────────────────────────────────────────────────

  onScannerTargetKeydown(event: KeyboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.commitScannerBuffer();
      return;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      this.appendScannerCharacter(event.key);
    }
  }

  onScannerTargetInput(event: Event): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    input.value = '';
    if (value) {
      this.scanBuffer = value;
      this.scannerFirstCharacterAt = Date.now();
      this.scannerLastCharacterAt = this.scannerFirstCharacterAt;
      this.restartScannerBufferReset(true, true);
    }
  }

  onScannerTargetPaste(event: ClipboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    const pastedBarcode = event.clipboardData?.getData('text')?.trim();
    if (pastedBarcode) {
      event.preventDefault();
      this.acceptCapturedBarcode(pastedBarcode);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    if (event.target === this.scannerInput?.nativeElement) return;

    if (this.isInteractiveNonBarcodeTarget(event.target)) return;

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.commitScannerBuffer();
      return;
    }

    if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) return;

    event.preventDefault();
    this.appendScannerCharacter(event.key);
  }

  private focusScannerTarget(): void {
    setTimeout(() => {
      if (this.enterManually) {
        this.capturedInput?.nativeElement.focus();
        this.capturedInput?.nativeElement.select();
      } else if (this.isCapturingBarcode) {
        this.scannerInput?.nativeElement.focus();
      }
    });
  }

  private acceptCapturedBarcode(barcode: string): void {
    this.measuredMudContainer = barcode.trim();
    this.enterManually = false;
    this.containerEntered = true;
    this.clearScannerBuffer();
    this.cdr.markForCheck();
  }

  private normalizeBarcode(value: string): string {
    return (value ?? '').replace(/[\r\n\t]/g, '').trim();
  }

  private isInteractiveNonBarcodeTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el) return false;

    const scannerEl = this.scannerInput?.nativeElement;
    if (scannerEl && (el === scannerEl || scannerEl.contains(el))) return false;

    const capturedEl = this.capturedInput?.nativeElement;
    if (capturedEl && (el === capturedEl || capturedEl.contains(el))) return false;

    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el.closest('[contenteditable="true"]');
  }

  private appendScannerCharacter(character: string): void {
    const now = Date.now();
    if (this.scannerLastCharacterAt != null && now - this.scannerLastCharacterAt > this.scannerInterKeyTimeoutMs) {
      this.clearScannerBuffer();
    }
    if (this.scannerFirstCharacterAt == null) {
      this.scannerFirstCharacterAt = now;
    }
    this.scannerLastCharacterAt = now;
    this.scanBuffer += character;
    this.restartScannerBufferReset(true);
  }

  private commitScannerBuffer(allowWholeValue = false): void {
    const scannedBarcode = this.scanBuffer.trim();
    const isScannerBurst = allowWholeValue || this.isLikelyScannerBurst();
    this.clearScannerBuffer();
    if (scannedBarcode && isScannerBurst) {
      this.acceptCapturedBarcode(scannedBarcode);
    }
  }

  private isLikelyScannerBurst(): boolean {
    if (this.scanBuffer.length < this.scannerMinimumLength
      || this.scannerFirstCharacterAt == null
      || this.scannerLastCharacterAt == null) {
      return false;
    }
    const intervals = Math.max(1, this.scanBuffer.length - 1);
    return (this.scannerLastCharacterAt - this.scannerFirstCharacterAt) / intervals
      <= this.scannerMaximumAverageInterKeyMs;
  }

  private restartScannerBufferReset(commitAfterTimeout = false, allowWholeValue = false): void {
    if (this.scanBufferResetHandle) {
      clearTimeout(this.scanBufferResetHandle);
    }
    this.scanBufferResetHandle = setTimeout(() => {
      this.scanBufferResetHandle = null;
      if (commitAfterTimeout) {
        this.commitScannerBuffer(allowWholeValue);
        return;
      }
      this.clearScannerBuffer();
    }, this.scannerInterKeyTimeoutMs);
  }

  private clearScannerBuffer(): void {
    if (this.scanBufferResetHandle !== null) {
      clearTimeout(this.scanBufferResetHandle);
      this.scanBufferResetHandle = null;
    }
    this.scanBuffer = '';
    this.scannerFirstCharacterAt = null;
    this.scannerLastCharacterAt = null;
  }

  get isContainerAcceptable(): boolean {
    if (!this.measuredMudContainer) return true;
    
    const preferred = this.data.preferredMudContainer;
    // If no preferred container is specified, any container is acceptable
    if (!preferred || preferred.trim() === '') return true;
    
    const list = preferred.split(',').map(s => s.trim());
    return list.includes(this.measuredMudContainer.trim());
  }

  get isValid(): boolean {
    const hasBasicData = (
      this.measuredMudContainer !== undefined &&
      this.measuredMudContainer !== null &&
      this.measuredMudContainer.trim() !== ''
    );

    if (!hasBasicData) return false;

    // If container is not acceptable, comments must be provided
    if (!this.isContainerAcceptable) {
      if (!this.mudContainerComments || !this.mudContainerComments.trim()) {
        return false;
      }
    }

    return true;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.isValid) {
      const result: MudMeasureDialogResult = {
        measuredMudContainer: this.measuredMudContainer,
        mudContainerComments: this.mudContainerComments,
        replacePreferredContainer: this.replacePreferredContainer
      };
      this.dialogRef.close(result);
    }
  }
}
